(() => {
  const MOBILE_BREAKPOINT = '(max-width: 749px)';
  const HIDDEN_CLASS = 'is-hidden';
  const CONTACT_WIDGET_SELECTOR = '.owa-widget';
  const CONTACT_WIDGET_OFFSET_PX = 30;
  const CONTACT_WIDGET_OFFSET_ACTIVE = 'true';

  class OwaCartStickyCheckoutController {
    constructor() {
      this.footer = null;
      this.target = null;
      this.sticky = null;

      this.mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
      this.intersectionObserver = null;
      this.mutationObserver = null;
      this.unsubscribeCartUpdate = null;

      this.onMediaQueryChange = this.refresh.bind(this);
      this.onFallbackCheck = this.checkFallbackVisibility.bind(this);
      this.onFooterMutation = this.refresh.bind(this);
    }

    init() {
      if (!document.body.classList.contains('template-cart')) return;

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onMediaQueryChange);
      } else if (this.mediaQuery.addListener) {
        this.mediaQuery.addListener(this.onMediaQueryChange);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.refresh();
        });
      }

      this.ensureFooterObserver();
      this.refresh();
    }

    destroy() {
      this.restoreContactWidgetOffsetIfNeeded();
      this.disconnectVisibilityObservers();

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }

      if (this.unsubscribeCartUpdate) {
        this.unsubscribeCartUpdate();
        this.unsubscribeCartUpdate = null;
      }

      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.onMediaQueryChange);
      } else if (this.mediaQuery.removeListener) {
        this.mediaQuery.removeListener(this.onMediaQueryChange);
      }
    }

    refresh() {
      this.footer = document.getElementById('main-cart-footer');
      this.target = document.querySelector('[data-owa-checkout-target]');
      this.sticky = document.querySelector('[data-owa-sticky-checkout]');

      this.disconnectVisibilityObservers();

      if (!this.sticky) {
        this.restoreContactWidgetOffsetIfNeeded();
        return;
      }

      if (!this.shouldRun() || !this.target) {
        this.setStickyHidden(true);
        return;
      }

      if ('IntersectionObserver' in window) {
        this.checkFallbackVisibility();
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.setStickyHidden(this.shouldHide(entry.isIntersecting));
            });
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.target);
        return;
      }

      window.addEventListener('scroll', this.onFallbackCheck, { passive: true });
      window.addEventListener('resize', this.onFallbackCheck);
      this.checkFallbackVisibility();
    }

    shouldRun() {
      if (!document.body.classList.contains('template-cart')) return false;
      return this.mediaQuery.matches;
    }

    shouldHide(targetVisible) {
      if (!this.target) return true;
      if (this.target.hasAttribute('disabled')) return true;
      if (this.footer && this.footer.classList.contains('is-empty')) return true;
      return targetVisible;
    }

    setStickyHidden(hidden) {
      if (this.sticky) {
        this.sticky.classList.toggle(HIDDEN_CLASS, hidden);
      }

      if (this.shouldRun() || this.isContactWidgetOffsetActive()) {
        this.updateContactWidgetOffset(!hidden);
      }
    }

    updateContactWidgetOffset(stickyVisible) {
      const widget = this.getContactWidget();
      if (!widget) return;

      if (!widget.dataset.owaCartWidgetInlineTransition) {
        widget.dataset.owaCartWidgetInlineTransition = widget.style.transition || '';
      }

      if (!this.isContactWidgetOffsetActive(widget)) {
        const computedBottom = Number.parseFloat(window.getComputedStyle(widget).bottom);
        widget.dataset.owaCartWidgetBaseBottom = Number.isFinite(computedBottom) ? String(computedBottom) : '0';
      }

      if (stickyVisible && this.shouldRun()) {
        const baseBottom = Number.parseFloat(widget.dataset.owaCartWidgetBaseBottom || '0') || 0;
        widget.style.transition = 'bottom 0.3s ease';
        widget.style.bottom = `${baseBottom + CONTACT_WIDGET_OFFSET_PX}px`;
        widget.dataset.owaCartWidgetOffsetActive = CONTACT_WIDGET_OFFSET_ACTIVE;
        return;
      }

      const baseBottom = Number.parseFloat(widget.dataset.owaCartWidgetBaseBottom || '');
      widget.style.bottom = Number.isFinite(baseBottom) ? `${baseBottom}px` : '';
      widget.style.transition = widget.dataset.owaCartWidgetInlineTransition || '';
      widget.dataset.owaCartWidgetOffsetActive = 'false';
    }

    getContactWidget() {
      return document.querySelector(CONTACT_WIDGET_SELECTOR);
    }

    isContactWidgetOffsetActive(widget = this.getContactWidget()) {
      return !!widget && widget.dataset.owaCartWidgetOffsetActive === CONTACT_WIDGET_OFFSET_ACTIVE;
    }

    restoreContactWidgetOffsetIfNeeded() {
      if (!this.isContactWidgetOffsetActive()) return;
      this.updateContactWidgetOffset(false);
    }

    checkFallbackVisibility() {
      if (!this.target) return;

      const rect = this.target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const isVisible = rect.top < viewportHeight && rect.bottom > 0;

      this.setStickyHidden(this.shouldHide(isVisible));
    }

    disconnectVisibilityObservers() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      window.removeEventListener('scroll', this.onFallbackCheck);
      window.removeEventListener('resize', this.onFallbackCheck);
    }

    ensureFooterObserver() {
      this.footer = document.getElementById('main-cart-footer');
      if (!this.footer) return;

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }

      this.mutationObserver = new MutationObserver(this.onFooterMutation);
      this.mutationObserver.observe(this.footer, { childList: true, subtree: true });
    }
  }

  function initOwaCartStickyCheckout() {
    if (!document.body.classList.contains('template-cart')) return;

    if (window.__owaCartStickyCheckoutController) {
      window.__owaCartStickyCheckoutController.refresh();
      return;
    }

    const controller = new OwaCartStickyCheckoutController();
    controller.init();
    window.__owaCartStickyCheckoutController = controller;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOwaCartStickyCheckout);
  } else {
    initOwaCartStickyCheckout();
  }

  window.initOwaCartStickyCheckout = initOwaCartStickyCheckout;
})();
